import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { buildConsultationUrl, appendLanguageToEuFormUrl } from '../utils/urlEncoding';

interface ConsultationInProgressProps {
  /** Base URL for reopening guide window */
  guideBaseUrl: string;
  /** URL for the EU consultation form */
  euFormUrl: string;
  /** User data for encoding in guide URL */
  userData: {
    email: string;
    country: string;
    firstName?: string;
    lastName?: string;
  };
  /** Optional callback when user wants to abandon consultation */
  onAbandon?: () => void;
}

/**
 * ConsultationInProgress Component
 *
 * Displays in the ORIGINAL window after popup windows are launched.
 * Provides status information and reopen functionality for accidentally closed windows.
 */
export function ConsultationInProgress({
  guideBaseUrl,
  euFormUrl,
  userData,
  onAbandon
}: ConsultationInProgressProps) {
  const { t, i18n } = useTranslation('inProgress');

  /**
   * Reopens the guide window with encoded user data
   */
  const reopenGuideWindow = () => {
    console.log('[ConsultationInProgress] Reopening guide window...');

    const guideUrl = buildConsultationUrl(
      guideBaseUrl,
      userData.email,
      userData.country,
      userData.firstName,
      userData.lastName
    );

    const guideWindow = window.open(
      guideUrl,
      'ciwf-guide-popup',  // Same name as main launcher
      'width=500,height=800,scrollbars=yes,resizable=yes'
    );

    if (!guideWindow) {
      alert(t('alerts.allowPopupsGuide'));
      return;
    }

    console.log('[ConsultationInProgress] Guide window reopened');
  };

  /**
   * Reopens the EU form window
   */
  const reopenFormWindow = () => {
    console.log('[ConsultationInProgress] Reopening form window...');

    const currentLanguage = i18n.language || 'en';
    const urlWithLanguage = appendLanguageToEuFormUrl(euFormUrl, currentLanguage);

    const formWindow = window.open(
      urlWithLanguage,
      'eu-consultation-form',  // Same name as main launcher
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );

    if (!formWindow) {
      alert(t('alerts.allowPopupsForm'));
      return;
    }

    console.log('[ConsultationInProgress] Form window reopened');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-6">
      <div className="max-w-3xl w-full bg-white rounded-lg shadow-xl p-8">

        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative">
              <FileText className="w-16 h-16 text-primary" />
              <CheckCircle className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1 bg-white rounded-full" />
            </div>
            <div className="text-4xl text-gray-400">↔️</div>
            <div className="relative">
              <ExternalLink className="w-16 h-16 text-blue-600" />
              <CheckCircle className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1 bg-white rounded-full" />
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-3 font-[Sofia_Sans_Condensed] text-gray-900">
            {t('title')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('subtitle')}
          </p>
        </div>

        {/* Status Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-green-900 mb-2">
                {t('status.title')}
              </h2>
              <ul className="space-y-2 text-sm text-green-800">
                <li className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span><strong>{t('status.guideLabel')}</strong> {t('status.guideDesc')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  <span><strong>{t('status.formLabel')}</strong> {t('status.formDesc')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {t('instructions.title')}
          </h3>
          <ol className="space-y-2 text-sm text-blue-800 list-decimal list-inside">
            <li>{t('instructions.step1')}</li>
            <li>{t('instructions.step2')}</li>
            <li>{t('instructions.step3')}</li>
            <li>{t('instructions.step4')}</li>
            <li>{t('instructions.step5')}</li>
          </ol>
        </div>

        {/* Reopen Options */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-amber-900 mb-3">
            {t('reopen.title')}
          </h3>
          <p className="text-sm text-amber-800 mb-4">
            {t('reopen.description')}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={reopenGuideWindow}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
            >
              <FileText className="w-4 h-4" />
              {t('reopen.guideButton')}
            </button>
            <button
              onClick={reopenFormWindow}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              {t('reopen.formButton')}
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t pt-6 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {t('footer.note')}
            </p>
            {onAbandon && (
              <button
                onClick={onAbandon}
                className="text-sm text-red-600 hover:text-red-700 underline"
              >
                {t('footer.cancelButton')}
              </button>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 text-center">
            <strong>{t('help.tipLabel')}</strong> {t('help.tipText')}
          </p>
        </div>

      </div>
    </div>
  );
}
